import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { RouteAction } from '@core/action/action';
import { CustomValidators } from '@core/custom-validators/validators';
import { NomeTelasEnum } from '@core/models/dominio';
import { TipoContatoEnum } from '@core/models/entidade';
import { NomePaginaEnum } from '@core/models/nome-pagina.enum';
import { AreaAtuacaoService } from '@core/services/areaAtuacaoService';
import { CEPService } from '@core/services/cep.service';
import { CidadeService } from '@core/services/cidade.service';
import { MessageService } from '@core/services/message.service';
import { PaisService } from '@core/services/pais.service';
import { TipoContatoService } from '@core/services/tipocontato.service';
import { UFService } from '@core/services/uf.service';
import { EntidadeSharedDataService } from '@modules/entidade/entidade-shared-data.service';
import { ddiIguais, normalizarDdiParaSelect } from '@shared/util/ddi-select.util';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-outros-contatos-form',
  templateUrl: './outros-contatos-form.component.html',
  styleUrls: ['./outros-contatos-form.component.scss']
})
export class OutrosContatosFormComponent implements OnInit, OnChanges {

  @Input() contato: any;
  @Input() dataSet: any;
  @Input() paginaEntidade: boolean = false;
  @Input() nomePagina!: NomePaginaEnum;
  @Output() dadosOutrosContatosEmit = new EventEmitter<any>();
  @Output() dadosTipoContatoIdEmit = new EventEmitter<any>();

  outrosContatosForm!: FormGroup;
  contatoEditado: any;
  contatos: any[] = [];
  cidadeList: any[] = [];
  tipoContatoList: any[] = [];
  paisesList: any[] = [];
  ufList: any[] = [];
  ddiList: any[] = [];
  areaAtuacaoList: any[] = [];

  cep: string = '';
  tipoContatoSelecionada: any;
  acao!: RouteAction;
  inserir!: boolean;
  editar!: boolean;
  visualizar!: boolean;
  defaultPais: any;
  tipoContatoEnum = TipoContatoEnum;
  existeEndereco: boolean = false;

  cepToZipCode: boolean = false;

  constructor(
    private fb: FormBuilder,
    private ufService: UFService,
    private paisService: PaisService,
    private tipoContatoService: TipoContatoService,
    private route: ActivatedRoute,
    private cepService: CEPService,
    private messageService: MessageService,
    private cidadeService: CidadeService,
    private areaAtuacaoService: AreaAtuacaoService,
    private entidadeSharedDataService: EntidadeSharedDataService
  ) { }

  ngAfterViewInit(): void {
    this.entidadeSharedDataService?.updateEmpresaEstrangeira$.subscribe(res => {
      if (res) {
        this.cepToZipCode = true;
      } else {
        this.cepToZipCode = false;
      }
    })
  }

  ngOnInit() {
    this.carregarListas();
    this.construirForm();

    this.enderecoUf?.valueChanges.subscribe((val: any) => {
      if (val?.id) {
        this.cidadeService.getCidadesByUf(val.id).subscribe(data => {
          this.cidadeList = data.sort((a: { nome: string; }, b: { nome: any; }) => a.nome.localeCompare(b.nome));
        });
      } else this.cidadeList = [];
    })

    this.paisControl?.valueChanges.subscribe((val: any) => {
      if (val?.id) {
        // console.log(val)
        this.outrosContatosForm.patchValue({ddi: val?.ddi});
      }
    });
  };

  ngOnChanges(): void {
    if(this.contato) {
      this.inserir = false;
      this.preencherContato(this.contato);
    } else {
      this.inserir = true;
    }
  }

  compareDdi = (a: unknown, b: unknown): boolean => ddiIguais(a, b);

  private preencherContato(contato: any): void {
    if (!this.outrosContatosForm) {
      return;
    }
    const tipoContato = this.resolverTipoContato(contato?.tipoContato);
    const email = this.resolverEmail(contato?.email);
    const ddi = normalizarDdiParaSelect(contato?.ddi, this.ddiList);

    this.outrosContatosForm.patchValue({
      ...contato,
      tipoContato,
      email,
      ddi
    });
    this.outrosContatosForm.patchValue({ ddi }, { emitEvent: false });

    if (tipoContato) {
      this.onTipoContatoChange(tipoContato);
    }
  }

  private resolverTipoContato(tipoContato: any): any {
    if (!tipoContato) {
      return tipoContato;
    }
    return this.tipoContatoList.find((tipo: any) => Number(tipo?.id) === Number(tipoContato?.id)) || tipoContato;
  }

  private resolverEmail(email: unknown): string | null {
    if (email == null || email === '') {
      return null;
    }
    if (typeof email === 'string') {
      return email;
    }
    return (email as { email?: string }).email ?? null;
  }

  construirForm() {
    this.outrosContatosForm = this.fb.group({
      id:[''],
      pais: [''],
      enderecoUf: [''],
      enderecoMunicipio: [''],
      enderecoBairro: [''],
      enderecoCep: [''],
      enderecoNumero: [''],
      enderecoLogradouro: [''],
      enderecoComplemento: [''],
      informacoesAdicionais: [''],
      ddi: [''],
      telefone: ['', CustomValidators.telefone],
      telefoneAdicional: [''],
      ddiFax: [''],
      fax: [''],
      faxAdicional: [''],
      siteUrl: [''],
      ramal: [''],
      tipoContato: ['', Validators.required],
      email: [''],
      escolherContato: [''],
      nomeContato: [''],
      observacao: [''],
      areaAtuacao: [''],
    });
  }

  carregarListas(): void {
    this.paisService.listar().subscribe((ddi: any) => {
      this.ddiList = ddi;
      if (this.contato?.ddi) {
        this.preencherContato(this.contato);
      }
    });

    const nomePaginaCapitalize = this.nomePagina.charAt(0).toUpperCase() + this.nomePagina.slice(1);
    this.tipoContatoService.tipoContatoTelaPermitidaList(nomePaginaCapitalize).subscribe((tipoContato: any) => {
      this.tipoContatoList = tipoContato;
      if (this.contato) {
        this.preencherContato(this.contato);
      }
    });

    forkJoin({
      enderecoUf: this.ufService.listar(),
      pais: this.paisService.listar(),
      areaAtuacao: this.areaAtuacaoService.listarAreaAtuacaoCombo()
    }).subscribe(({ enderecoUf, pais, areaAtuacao }) => {
      this.ufList = enderecoUf.sort((a, b) => a.nome.localeCompare(b.nome));
      this.paisesList = pais;
      this.areaAtuacaoList = areaAtuacao;
      const defaultPais = this.paisesList.find(pais => pais.nome === 'Brasil');
      if (defaultPais && this.inserir) {
        this.outrosContatosForm.patchValue({
          pais: defaultPais
        });
      }
    });
  }

  onTipoContatoChange(value: any): void {
    if (value !== undefined) {
      this.tipoContatoSelecionada = this.tipoContatoList.find((tipo: any) => tipo?.id === value?.id);
      this.updateFormFields();
    }
  }

  updateFormFields(): void {
    this.resetValidators();

    switch (this.tipoContatoSelecionada?.id) {
      case TipoContatoEnum.EMAIL:
        this.setEmailValidators();
        break;

      case TipoContatoEnum.TELEFONE:
        this.setTelefoneValidators();
        break;

      case TipoContatoEnum.ENDERECO:
        this.setEnderecoValidators();
        break;

      case TipoContatoEnum.OUTROS:
        this.setAreaValidators();
        break;

      default:
        break;
    }

    this.setFormState();
    this.updateFormValidity();
  }

  resetValidators(): void {
    this.outrosContatosForm.get('email')?.clearValidators();
    this.outrosContatosForm.get('telefone')?.clearValidators();
    this.outrosContatosForm.get('pais')?.clearValidators();
    this.outrosContatosForm.get('enderecoUf')?.clearValidators();
    this.outrosContatosForm.get('enderecoMunicipio')?.clearValidators();
    this.outrosContatosForm.get('enderecoBairro')?.clearValidators();
    this.outrosContatosForm.get('enderecoCep')?.clearValidators();
    this.outrosContatosForm.get('enderecoLogradouro')?.clearValidators();
    this.outrosContatosForm.get('areaAtuacao')?.clearValidators();
  }

  setEmailValidators(): void {
    this.outrosContatosForm.get('email')?.setValidators([
      Validators.required,
      Validators.email,
      Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}')
    ]);
  }

  setTelefoneValidators(): void {
    this.outrosContatosForm.get('telefone')?.setValidators([Validators.required]);

    if (this.existeEndereco) {
      const itemEndereco = this.dataSet.find((item: any) => item.tipoContato.id === TipoContatoEnum.ENDERECO);
      if (itemEndereco) {
        const ddiPais = this.ddiList.find((ddi: any) => ddi.id === itemEndereco.pais.id);
        if (ddiPais) {
          this.outrosContatosForm.patchValue({ ddi: ddiPais.ddi });
        }
      }
    }
  }

  setEnderecoValidators(): void {
    this.outrosContatosForm.get('pais')?.setValidators([Validators.required]);
    this.outrosContatosForm.get('enderecoUf')?.setValidators([Validators.required]);
    this.outrosContatosForm.get('enderecoMunicipio')?.setValidators([Validators.required]);
    this.outrosContatosForm.get('enderecoBairro')?.setValidators([Validators.required]);
    this.outrosContatosForm.get('enderecoCep')?.setValidators([Validators.required]);
    this.outrosContatosForm.get('enderecoLogradouro')?.setValidators([Validators.required]);
  }

  setAreaValidators(): void {
    this.outrosContatosForm.get('areaAtuacao')?.setValidators([Validators.required]);
  }

  updateFormValidity(): void {
    this.outrosContatosForm.get('email')?.updateValueAndValidity();
    this.outrosContatosForm.get('telefone')?.updateValueAndValidity();
    this.outrosContatosForm.get('pais')?.updateValueAndValidity();
    this.outrosContatosForm.get('enderecoUf')?.updateValueAndValidity();
    this.outrosContatosForm.get('enderecoMunicipio')?.updateValueAndValidity();
    this.outrosContatosForm.get('enderecoBairro')?.updateValueAndValidity();
    this.outrosContatosForm.get('enderecoCep')?.updateValueAndValidity();
    this.outrosContatosForm.get('enderecoLogradouro')?.updateValueAndValidity();
    this.outrosContatosForm.get('areaAtuacao')?.updateValueAndValidity();
  }

  compareFn(a: any, b: any): boolean {
    if (typeof a === 'boolean' && (typeof b === 'string' || typeof b === 'boolean')) {
      b = b === 'true';
    } if (a?.id !== undefined && b?.id !== undefined) {
      return Number(a.id) === Number(b.id);
    } else if (a?.codigo !== undefined && b?.codigo !== undefined) {
      return a.codigo === b.codigo;
    } else if (a?.id !== undefined && b !== undefined) {
      return a.id === b;
    } else if (a !== undefined && b?.id !== undefined) {
      return a === b?.id;
    } else if (a?.uf !== undefined && b?.sigla !== undefined) {
      // console.log('a.uf', a.uf);
      return a?.uf === b?.sigla
    } else if (a?.cidade !== undefined && b?.nome !== undefined) {
      // console.log('a.cidade', a.cidade);
      // console.log('b.nome', b.nome);
      return a?.cidade === b?.nome && a?.uf === b?.uf.sigla
    } else {
      return a === b;
    }
  }

  setFormState(): void {
    if (this.visualizar) {
      this.outrosContatosForm.disable();
    }
  }

  salvar(): void {
    if(this.outrosContatosForm.valid) {
      let objEnvio = { ...this.outrosContatosForm.getRawValue() };

      this.dadosOutrosContatosEmit.emit(objEnvio);
      this.reset();
    }
  }

  reset() {
    this.inserir = true;

    // Object.keys(this.outrosContatosForm.controls).forEach(key => {
    //   const control = this.outrosContatosForm.get(key);
    //   if (control) {
    //     control.clearValidators();
    //     control.updateValueAndValidity({ emitEvent: true, onlySelf: true });
    //   }
    // });

    this.outrosContatosForm.reset();

    Object.keys(this.outrosContatosForm.controls).forEach(key => {
      const control = this.outrosContatosForm.get(key);
      if (control) {
        switch (key) {
          case 'tipoContato':
            control.setValidators(Validators.required);
            break;
          case 'email':
            control.setValidators([Validators.email, Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}')]);
            break;
          default:
            break;
        }
        control.markAsPristine();
        control.markAsUntouched();
        control.updateValueAndValidity({ emitEvent: false, onlySelf: true });
      }
    });
  }

  onCepBlur(): void {
    const cep = this.outrosContatosForm.get('enderecoCep')?.value;
    if (cep) {
      this.fetchCepData(cep);
    }
  }

  fetchCepData(cep: string): void {
    this.cepService.buscarEndereco(cep).subscribe(
      (data: any) => {
        if (!data.erro) {
          // console.log('CEP encontrado', data);

          // Encontrar a UF correspondente na lista de UFs
          const ufSelecionada = this.ufList.find(uf => uf.sigla === data.uf);
           // console.log('ufSelecionada', ufSelecionada);
          if (ufSelecionada && (!this.enderecoUf?.value || this.enderecoUf?.value.id !== ufSelecionada.id)) {
            this.enderecoUf?.patchValue(ufSelecionada); // Atualiza o valor do select de UF
            this.cidadeService.getCidadesByUf(ufSelecionada.id).subscribe(cidades => {
              this.cidadeList = cidades.sort((a: { nome: string; }, b: { nome: any; }) => a.nome.localeCompare(b.nome));
              this.setMunicipio(data); // Chamada para configurar a cidade após a atualização
            });
          } else {
            this.setMunicipio(data); // Caso a UF já esteja selecionada
          }

          this.outrosContatosForm.patchValue({
            enderecoLogradouro: data.logradouro,
            enderecoBairro: data.bairro,
          });
        } else {
          console.error('CEP não encontrado');
        }
      },
      (error) => {
        console.error('Erro ao buscar o CEP', error);
      }
    );
  }

  setMunicipio(data: any): void {
    const municipioSelecionado = this.cidadeList.find(cidade =>
      cidade.nome === data.cidade && cidade.uf.sigla === data.uf
    );

    this.outrosContatosForm.patchValue({
      enderecoMunicipio: municipioSelecionado || null,
      enderecoUf: municipioSelecionado?.uf || null,
    });
  }

  get paisControl() {
    return this.outrosContatosForm.get('pais');
  }

  get tipoContatoId() {
    const tipoContato = this.outrosContatosForm.get('tipoContato')?.value;
    this.dadosTipoContatoIdEmit.emit(tipoContato?.id);
    return tipoContato?.id;
  }

  get enderecoUf() {
    return this.outrosContatosForm.get('enderecoUf');
  }

  get escolherContatoControl() {
    return this.outrosContatosForm.get('escolherContato');
  }
}
